import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import map from 'lodash/map';
import Points from './Points';
import Text from '../components/atoms/Text';
import ImagePreview from './ImagePreview';
import { getConceptScore, getConcepts } from '../data/Concepts';

const Concept = styled.div`
	background-color: ${(props) => props.theme.colors.white};
	border-radius: 1em;
	margin: 1em;
	padding: 0.25em 1em;
	& h2,
	h3 {
		color: ${(props) => props.theme.colors.darkBlue};
	}
	& dt {
		font-weight: bold;
	}
	& dt:after {
		content: ':';
	}
	& dd {
		margin-bottom: 0.25em;
	}
`;

interface Props {}

const PersonaList: FunctionComponent<Props> = () => {
	const { t } = useTranslation();

	const conceptItems = useMemo(() => {
		return map(getConcepts(), (concept) => {
			const score = getConceptScore(concept);
			return (
				<Concept key={concept.name}>
					<h2>{concept.name}</h2>
					<h3>
						<Trans>data.points</Trans>
					</h3>
					<dl>
						<dt>Environment</dt>
						<dd>
							<Points value={score.environment} />
						</dd>
						<dt>Quality Of Life</dt>
						<dd>
							<Points value={score.qualityOfLife} />
						</dd>
						<dt>Happiness</dt>
						<dd>
							<Points value={score.happiness} />
						</dd>
					</dl>
					<h3>
						<Trans>data.images</Trans>
					</h3>
					<dl>
						<dt>Icon</dt>
						<dd>
							<ImagePreview imageKey={concept.images.icon} />
						</dd>
						<dt>Map Overlay</dt>
						<dd>
							<table>
								<thead>
									<tr>
										<th>Inactive</th>
										<th>Active</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<ImagePreview
												imageKey={concept.images.overlay?.map?.inactive?.key}
												screen
												info={`Priority: ${
													concept.images.overlay?.map?.inactive?.priority ?? 0
												}`}
											/>
										</td>
										<td>
											<ImagePreview
												imageKey={concept.images.overlay?.map?.active?.key}
												screen
												info={`Priority: ${
													concept.images.overlay?.map?.active?.priority ?? 0
												}`}
											/>
										</td>
									</tr>
								</tbody>
							</table>
						</dd>
						<dt>Effect</dt>
						<dd>
							<table>
								<thead>
									<tr>
										<th>Before</th>
										<th>After</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<ImagePreview imageKey={concept.images.before} screen />
										</td>
										<td>
											<ImagePreview imageKey={concept.images.after} screen />
										</td>
									</tr>
								</tbody>
							</table>
						</dd>
					</dl>
					<h3>
						<Trans>data.texts</Trans>
					</h3>
					<dl>
						<dt>Category</dt>
						<dd>
							<Trans>{concept.category}</Trans>
						</dd>
						<dt>Name</dt>
						<dd>
							<Trans>{concept.texts.name}</Trans>
						</dd>
						<dt>Short Description</dt>
						<dd>
							<Text content={t(concept.texts.shortDescription)} />
						</dd>
						<dt>Description</dt>
						<dd>
							<Text
								content={t(concept.texts.description, {
									returnObjects: true
								})}
							/>
						</dd>
						<dt>Pro Argument</dt>
						<dd>
							<Text content={t(concept.texts.proArgument)} />
						</dd>
						<dt>Contra Argument</dt>
						<dd>
							<Text content={t(concept.texts.contraArgument)} />
						</dd>
						<dt>Result Title</dt>
						<dd>
							<Text content={t(concept.texts.resultTitle)} />
						</dd>
						<dt>Result Text</dt>
						<dd>
							<Text content={t(concept.texts.resultText)} />
						</dd>
					</dl>
				</Concept>
			);
		});
	}, [t]);

	return <>{conceptItems}</>;
};

export default PersonaList;
